import {
    useLazyQuery,
    useMutation,
    useQuery,
    useReactiveVar,
} from '@apollo/client'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { CurrentUserActiveFirmQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import {
    AddUpdateBestPracticeResponse,
    BestPracticeData,
    BestPracticeDataResponse,
    RemoveBestPracticeResponse,
} from '@deltek/specification-client/core/Models/bestPractice'
import { CurrentUserActiveFirmResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { BestPracticesIcons } from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesIcons'
import { BestPracticesSetting } from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesSetting'
import {
    compareBestPracticesMutation,
    getIsBP,
} from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesSettingUtil'
import { ProductListingManufacturerOptionItem } from '@deltek/specification-client/core/Widgets/ProductListing/Card/ProductListingManufacturerOptionItem'
import { Button } from '@material-ui/core'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import { Theme, makeStyles } from '@material-ui/core/styles'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import MoreVert from '@material-ui/icons/MoreVert'
import {
    AddUpdateBestPracticeMutation,
    RemoveBestPracticeMutation,
} from 'api/graphQL/mutations/bestPractices.mutations'
import { BestPracticeQuery } from 'api/graphQL/queries/bestPractices.queries'

import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import SingleLineEllipsis from '@deltek/specification-client/core/Feedbacks/SingleLineEllipsis'
import {
    Iteration,
    ProjectElementNode,
} from '@deltek/specification-client/core/Models/productListingDetail'
import { OMElementLibraryElementType } from '@deltek/specification-client/core/Models/projectElement'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import {
    CheckHasPublishedIterationsQuery,
    GetProductFamilyIterationQuery,
} from 'api/graphQL/queries/projectElements.queries'
import { ElementType } from 'api/graphQL/reactiveVars/element'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    isOmWorkspaceUser,
    setReactiveWorkspaceContents,
} from 'api/graphQL/reactiveVars/workspaceContent'
import clsx from 'clsx'
import ManufacturarOptionIcon from 'components/ElementsTree/components/ElementsPanel/ManufacturarOptionIcon'
import SingleLineEllipsisForSearch from 'components/ElementsTree/components/SingleLineElipsisForSearch/SingleLineEllipsisForSearch'
import { useElementToTreeMapper } from 'components/WorkspaceEditor/hooks/useElementToTreeMapper'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { useMemo, useRef, useState } from 'react'
import Highlighter from 'react-highlight-words'
import {
    canShowBestPracticeSetting,
    useIsBestPracticeTagDisabled,
} from 'shared/utils/bestPracticeUtils'
import { escapeRegexCharacters } from 'shared/utils/stringUtils'
import { commonColors } from 'xspectation/shared/constants'
import { ProjectPageContext } from '../../../ProjectPage/components/ProjectPageContext'
import {
    manufacturerProductTypeFillNotSelected,
    manufacturerProductTypeFillSelected,
    manufacturerProductTypeOutlineNotSelected,
    manufacturerProductTypeOutlineSelected,
} from '../../../colorVars'
import { onEnterKeyDown } from '../Common/utils'
import { CompleteMenuItemConfig, WorkspacePopover } from '../WorkspacePopover'
import DeactivatePTDropdown from './DeactivatePTDropdown'
import WorkspaceVersionDropdown from './WorkspaceVersionDropdown'
import { showElementCardTitleInSingleLine } from './elementCardUtil'
export interface ElementItemCardStyleProps {
    isHighlighted?: boolean
    darkCarret?: boolean
    isL4L5Assembly?: boolean
    calledFromImportPage?: boolean
    contentTeamFromStudioPage?: boolean
    isOpaque?: boolean
    isBasedFromManufacturerOption?: boolean
    isInProject?: boolean
    calledFromImportDuplicateFamily?: boolean
    isBetaWorkspaceToggleEnabled?: boolean
    elementType?: string
    isBestPracticeIconShown?: boolean
    isDeactivated?: boolean
}

const useElementCardStyles = makeStyles((theme: Theme) => ({
    outerPaper: {
        position: 'relative',
        left: pxToRem(4),
        height: '100%',
        paddingRight: (props: ElementItemCardStyleProps) =>
            props.isBetaWorkspaceToggleEnabled
                ? theme.spacing(0.5)
                : theme.spacing(1.5),
    },
    checkedIcon: {
        height: pxToRem(16),
        width: pxToRem(16),
        marginRight: theme.spacing(1),
    },
    cardItemContainer: (props: ElementItemCardStyleProps) => ({
        position: 'relative',
        width: '100%',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0.75, 1.5),
        minHeight: props.isBetaWorkspaceToggleEnabled ? 47 : 50,
        '& .MuiButton-endIcon': {
            marginRight: -theme.spacing(1),
        },
    }),
    addedItemForAssociation: {
        backgroundColor: '#FBB41F !important',
        color: '#000000 !important',
    },

    titleTextElementCard: {
        fontSize: (props: ElementItemCardStyleProps) =>
            props.isBetaWorkspaceToggleEnabled ? pxToRem(12) : pxToRem(14),
        lineHeight: 1.4,
        fontWeight: (props: ElementItemCardStyleProps) =>
            props.calledFromImportDuplicateFamily ? 500 : 'inherit',
    },

    elTitleText: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    titleTextMargin: { marginLeft: theme.spacing(0.5) },
    subtitleText: {
        flex: 1,
        flexGrow: 1,
        fontSize: pxToRem(11),
    },
    cardTextContainer: {
        left: theme.spacing(2),
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        overflow: 'hidden',
        width: '100%',
    },
    leafNodeElement: {
        background: '#A78966 !important',
        border: '1px solid #A78966',

        color: '#FFFFFF !important',
        '&:hover': {
            background: '#A78966 !important',
            border: '1px solid #A78966',
            '& svg': {
                color: (props: ElementItemCardStyleProps) => {
                    return props.isHighlighted
                        ? 'rgba(0, 0, 0)'
                        : 'rgba(0, 0, 0, 0.54)'
                },
            },
        },
    },
    menuText: {
        fontSize: pxToRem(14),
        color: (props: ElementItemCardStyleProps) =>
            props.isBetaWorkspaceToggleEnabled ? '#424242' : '#211F20',
        cursor: 'pointer',
        marginBottom: theme.spacing(0.5),
    },
    menuTextDisabled: {
        cursor: 'default',
        opacity: 0.5,
    },
    disabledMenuItem: {
        opacity: (props: ElementItemCardStyleProps) =>
            props.isInProject ? 1 : 0.5,
        fontSize: pxToRem(20),
    },
    gridItem: {
        minWidth: theme.spacing(4),
        lineHeight: 0,
        textAlign: 'center',
        fontSize: pxToRem(22),
    },
    BPIcons: {
        textAlign: 'center',
        color: theme.palette.common.white,
        position: 'absolute',
        right: theme.spacing(2),
        display: 'flex',
        flexDirection: 'row-reverse',
        alignItems: 'center',
        justifyContent: 'center',
    },

    nonselectableCard: (props: ElementItemCardStyleProps) => ({
        background: props?.isBetaWorkspaceToggleEnabled ? '#DBE3F2' : '#DBDFE0',
        border: props?.isBetaWorkspaceToggleEnabled
            ? '1px solid #B5C7EA'
            : '1px solid #C7C7C7',
        fontWeight: props?.isBetaWorkspaceToggleEnabled ? 500 : 400,
        color: props.isBetaWorkspaceToggleEnabled
            ? '#314959'
            : theme.palette.common.black,
        '&:hover': {
            background: props?.isBetaWorkspaceToggleEnabled
                ? '#B5C7EA99'
                : '#F7F7F7',
            border: props.isBetaWorkspaceToggleEnabled
                ? '1px solid #B5C7EA'
                : '1px solid #DBDFE0',
            color: props.isBetaWorkspaceToggleEnabled
                ? '#314959'
                : theme.palette.common.black,
            '& svg': {
                color: (props: ElementItemCardStyleProps) => {
                    return props.isHighlighted
                        ? 'rgba(0, 0, 0)'
                        : 'rgba(0, 0, 0, 0.54)'
                },
            },
        },
    }),
    nonSelectableExpanded: (props: ElementItemCardStyleProps) => ({
        background: props?.isBetaWorkspaceToggleEnabled ? '#DBE3F2' : '#8E8E8E',
        border: props?.isBetaWorkspaceToggleEnabled
            ? '1px solid #B5C7EA'
            : '1px solid #777777',
        color: props?.isBetaWorkspaceToggleEnabled
            ? '#314959'
            : theme.palette.common.white,
    }),
    omDeactivatedPTCard: (props: ElementItemCardStyleProps) => ({
        background: props?.isBetaWorkspaceToggleEnabled
            ? '#FFFFFF !important'
            : '#55555580 !important',
        color: props?.isBetaWorkspaceToggleEnabled
            ? '#314959 !important'
            : theme.palette.common.white,
        border: props.isBetaWorkspaceToggleEnabled
            ? '1px solid #A8BCCA80 !important'
            : '1px solid #ffffff !important',
        '&:hover': {
            background: props.isBetaWorkspaceToggleEnabled
                ? '#FFFFFF50 !important'
                : 'inherit',
        },
    }),
    selectableCard: (props: ElementItemCardStyleProps) => ({
        color: props.isBetaWorkspaceToggleEnabled
            ? '#314959'
            : theme.palette.common.white,
        background: props?.isBetaWorkspaceToggleEnabled
            ? '#DBE3F2'
            : props.contentTeamFromStudioPage
            ? '#394659'
            : '#343A3F',
        border: props?.isBetaWorkspaceToggleEnabled
            ? '1px solid #B5C7EA'
            : props.contentTeamFromStudioPage
            ? '1px solid #ffffff'
            : '1px solid #526B7C',
        transition: '100ms',
        fontWeight: props?.isBetaWorkspaceToggleEnabled ? 500 : 400,
        '&:hover': {
            background: props?.isBetaWorkspaceToggleEnabled
                ? '#B5C7EA99'
                : '#304959',
            border: (props: ElementItemCardStyleProps) =>
                props?.isBetaWorkspaceToggleEnabled
                    ? '1px solid #B5C7EA'
                    : props.contentTeamFromStudioPage
                    ? '1px solid #ffffff'
                    : '1px solid #343A3F',
        },
    }),
    inProjectCard: {
        background: (props: ElementItemCardStyleProps) =>
            props.calledFromImportPage
                ? theme.palette.common.white
                : props.isBasedFromManufacturerOption
                ? manufacturerProductTypeFillNotSelected
                : props.isBetaWorkspaceToggleEnabled
                ? '#475E8A'
                : props.contentTeamFromStudioPage
                ? '#394659'
                : '#30B27E',
        color: (props: ElementItemCardStyleProps) =>
            props.calledFromImportPage
                ? theme.palette.common.black
                : theme.palette.common.white,
        border: (props: ElementItemCardStyleProps) =>
            props.calledFromImportPage
                ? '1px solid #DBDFE0'
                : props.isBasedFromManufacturerOption
                ? `1px solid ${manufacturerProductTypeOutlineNotSelected}`
                : props.isBetaWorkspaceToggleEnabled
                ? '1px solid #475E8A'
                : props.contentTeamFromStudioPage
                ? '1px solid #ffffff'
                : '1px solid #1C9363',
        borderRadius: pxToRem(4),

        '&:hover': {
            background: (props: ElementItemCardStyleProps) =>
                props.calledFromImportPage
                    ? '#F7F7F7'
                    : props.isBasedFromManufacturerOption
                    ? manufacturerProductTypeFillNotSelected
                    : props.isBetaWorkspaceToggleEnabled
                    ? '#2F4674'
                    : props.contentTeamFromStudioPage
                    ? props?.isDeactivated
                        ? '#c7c5c5 !important'
                        : '#475e7e'
                    : '#38CE92',
            border: (props: ElementItemCardStyleProps) =>
                props.calledFromImportPage
                    ? '1px solid #F7F7F7'
                    : props.isBasedFromManufacturerOption
                    ? `1px solid ${manufacturerProductTypeOutlineNotSelected}`
                    : props.isBetaWorkspaceToggleEnabled
                    ? '1px solid #475E8A'
                    : props.contentTeamFromStudioPage
                    ? '1px solid #ffffff'
                    : '1px solid #1C9363',
        },
    },
    manufacturerIcon: {
        transform: 'scale(0.625)',
        height: pxToRem(12),
        marginBottom: theme.spacing(0.25),
        marginRight: theme.spacing(0.25),
        paddingTop: (props: ElementItemCardStyleProps) =>
            props.isBetaWorkspaceToggleEnabled ? 0 : theme.spacing(0.5),
    },
    elementItemFooter: {
        position: 'relative',
        display: 'flex',
        alignContent: 'space-between',
    },
    cardCarret: {
        color: (props: ElementItemCardStyleProps) => {
            return props.isBetaWorkspaceToggleEnabled && !props.isInProject
                ? '#314959'
                : props?.calledFromImportPage
                ? theme.palette.common.black
                : props.darkCarret
                ? props.isHighlighted
                    ? 'rgba(0, 0, 0)'
                    : 'rgba(0, 0, 0, 0.54)'
                : props.isHighlighted
                ? 'rgba(255, 255, 255)'
                : 'rgba(255, 255, 255, 0.54)'
        },
        marginLeft: -theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
        '&.MuiSvgIcon-root': {
            fontSize: pxToRem(25),
            '&:focus': {
                border: '2px solid #ffffff',
                borderRadius: '5px',
                outline: 'none',
            },
        },
    },
    inactiveCard: {
        color: '#5E5F61',
        backround: 'rgba(255, 255, 255, 0.25)',
        '&:hover': {
            color: theme.palette.common.white,
            background: '#304959',
            border: '1px solid #343A3F',
            '& svg': {
                color: (props: ElementItemCardStyleProps) => {
                    return props.isHighlighted
                        ? 'rgba(255, 255, 255)'
                        : 'rgba(255, 255, 255, 0.54)'
                },
            },
        },
    },
    selectedInProject: {
        background: (props: ElementItemCardStyleProps) => {
            if (props.calledFromImportPage) return theme.palette.common.white
            else if (props.isBasedFromManufacturerOption)
                return manufacturerProductTypeFillSelected
            else if (props.isBetaWorkspaceToggleEnabled)
                return '#2F4674 !important'
            return '#1C9363'
        },
        color: (props: ElementItemCardStyleProps) =>
            props.calledFromImportPage
                ? theme.palette.common.black
                : theme.palette.common.white,
        border: (props: ElementItemCardStyleProps) => {
            if (props.calledFromImportPage) return '1px solid #F7F7F7'
            else if (props.isBasedFromManufacturerOption)
                return `1px solid ${manufacturerProductTypeOutlineSelected}`
            else if (props.isBetaWorkspaceToggleEnabled)
                return '1px solid #475E8A'
            return '1px solid #30B27E'
        },
        '&:hover': {
            background: (props: ElementItemCardStyleProps) => {
                if (props.calledFromImportPage)
                    return theme.palette.common.white
                else if (props.isBasedFromManufacturerOption)
                    return manufacturerProductTypeFillSelected
                else if (props.isBetaWorkspaceToggleEnabled) return '#2F4674'
                return '#38CE92'
            },
            border: (props: ElementItemCardStyleProps) => {
                if (props.calledFromImportPage) return '1px solid #DBDFE0'
                else if (props.isBasedFromManufacturerOption)
                    return `1px solid ${manufacturerProductTypeOutlineSelected}`
                else if (props.isBetaWorkspaceToggleEnabled)
                    return '1px solid #475E8A'
                return '1px solid #1C9363'
            },
        },
    },
    selectedInSMM: {
        background: '#10151C !important',
        color: theme.palette.common.white,
        border: '1px solid #ffffff !important',
        '&:hover': {
            background: '#10151c5e',
            border: '1px solid #ffffff',
        },
    },
    selectedSMMItem: (props: ElementItemCardStyleProps) => ({
        background: props?.isDeactivated
            ? props.isBetaWorkspaceToggleEnabled
                ? '#FFFFFF !important'
                : '#616060b0 !important'
            : '#677b98 !important',
        color:
            props?.isDeactivated && props?.isBetaWorkspaceToggleEnabled
                ? '#314959'
                : theme.palette.common.white,
        border:
            props?.isDeactivated && props?.isBetaWorkspaceToggleEnabled
                ? '1px solid #A8BCCA50 !important'
                : '1px solid #ffffff !important',
        '&:hover': {
            background: props.isBetaWorkspaceToggleEnabled
                ? '#FFFFFF50'
                : '#10151c5e',
            border: '1px solid #ffffff',
        },
    }),
    selectedPTInSMM: {
        background: '#10151C !important',
        color: `${theme.palette.common.white} !important`,
        border: '1px solid #10151C !important',
        '& :hover': {
            background: '#10151C !important',
            color: `${theme.palette.common.white} !important`,
        },
    },
    selectedNotInProject: (props: ElementItemCardStyleProps) => ({
        background: props.isBetaWorkspaceToggleEnabled ? '#E5E9F1' : '#526B7C',
        color: props.isBetaWorkspaceToggleEnabled
            ? '#314959'
            : theme.palette.common.white,
        border: props.isBetaWorkspaceToggleEnabled
            ? '1px solid #A8BCCA80'
            : '1px solid #304959',
        fontWeight: props?.isBetaWorkspaceToggleEnabled ? 500 : 400,
    }),
    selectedCard: {
        background: (props: ElementItemCardStyleProps) => {
            if (props.calledFromImportPage) return theme.palette.common.white
            else if (props.isBasedFromManufacturerOption)
                return manufacturerProductTypeFillSelected
            else if (props.isBetaWorkspaceToggleEnabled) return '#475E8A'
            return '#0A8050'
        },

        border: (props: ElementItemCardStyleProps) => {
            if (props.calledFromImportPage) return '1px solid #F7F7F7'
            else if (props.isBasedFromManufacturerOption)
                return `1px solid ${manufacturerProductTypeOutlineSelected}`
            else if (props.isBetaWorkspaceToggleEnabled)
                return '1px solid #475E8A'
            return '1px solid #304959'
        },
    },
    whiteCarret: {
        color: (props: ElementItemCardStyleProps) =>
            props.isBetaWorkspaceToggleEnabled &&
            (!props.isInProject || props.isDeactivated)
                ? '#314959'
                : `${theme.palette.common.white} !important`,
    },
    chevronDownIcon: {
        transform: 'rotate(90deg)',
    },
    notSearchResultCard: {
        opacity: '25%',
    },
    bpPosition: {
        position: 'absolute',
        top: '50%',
        right: 0,
        transform: 'translateY(-50%)',
        fontSize: pxToRem(25),
        lineHeight: 0,
        opacity: (props: ElementItemCardStyleProps) =>
            props.isOpaque ? '25%' : '100%',
        color: (props: ElementItemCardStyleProps) =>
            props.darkCarret
                ? theme.palette.common.black
                : theme.palette.common.white,
    },
    moreIcon: {
        fill: theme.palette.common.white,
        color: theme.palette.common.white,
        '&:focus': {
            borderRadius: '3px',
            outline: '1px solid #ffffff',
        },
    },
    moreIconBlack: (props: ElementItemCardStyleProps) => ({
        fill:
            props.isBetaWorkspaceToggleEnabled && !props.isInProject
                ? '#314959'
                : theme.palette.common.black,
        color:
            props.isBetaWorkspaceToggleEnabled && !props.isInProject
                ? '#314959'
                : theme.palette.common.black,
    }),
    addCircleIcon: {
        transform: 'translateX(-100%)', // negative value to overlap it with the left edge of element node container
        position: 'absolute',
        background: theme.palette.common.white,
        color: (props: ElementItemCardStyleProps) =>
            props.isBetaWorkspaceToggleEnabled ? '#314959' : '#304959',

        borderRadius: '50%',
    },
    addIconWithIndication: {
        marginBottom: theme.spacing(1.875),
        zIndex: 2,
    },
    ellipsisBtn: {
        minWidth: 0,
        padding: 0,
    },
    importIndication: {
        width: 24,
        height: '100%',
        color: (props: ElementItemCardStyleProps) =>
            props.isBetaWorkspaceToggleEnabled
                ? '#314959'
                : theme.palette.common.white,
        zIndex: 1,
        position: 'absolute',
        left: (props: ElementItemCardStyleProps) =>
            props.isBetaWorkspaceToggleEnabled ? -12 : -15,
        display: 'flex',
        alignItems: 'center',
    },
    importIndicationInner: (props: ElementItemCardStyleProps) => ({
        justifyContent: 'center',
        height: props.isBetaWorkspaceToggleEnabled ? 16 : 20,
        backgroundColor: props.isBetaWorkspaceToggleEnabled
            ? theme.palette.common.white
            : '#526B7C',
        border: props.isBetaWorkspaceToggleEnabled
            ? '2px solid #314959'
            : '2px solid white',
        borderRadius: pxToRem(4),
        alignItems: 'center',
        display: 'flex',
        fontSize: props.isBetaWorkspaceToggleEnabled
            ? pxToRem(10)
            : pxToRem(12),
        width: props.isBetaWorkspaceToggleEnabled ? 24 : 25,
        lineHeight: props.isBetaWorkspaceToggleEnabled ? 14 : 14.06,
    }),
    importIndicationEL: {
        width: 40,
        right: 10,
        left: 'auto',
    },
    importIndicationInnerEL: {
        height: 35,
        width: 35,
        borderRadius: 100,
        pointerEvents: 'none',
    },
    importIndicationInnerWithBulkAdd: {
        marginTop: theme.spacing(3),
        marginLeft: -theme.spacing(0.25),
    },
    duplicateImportFamilyBucket: {
        background: commonColors.rhinoShaleBlue,
        color: theme.palette.common.white,
        border: '1px solid #526B7C',
        transition: '100ms',
        '&:hover': {
            background: '#304959',
            border: '1px solid #ffffff',
        },
    },
    duplicateImportLeafNode: {
        background: theme.palette.common.white,
        color: commonColors.rhinoShaleBlue,
        border: '1px solid #394659 !important',
        height: '100%',
    },
    iconsContainer: {
        position: 'absolute',
        width: '100%',
        right: theme.spacing(-0.75),
        display: 'flex',
        justifyContent: 'flex-end',
    },
    bpSpacer: {
        width: theme.spacing(0.75),
    },
    bPIconBlack: {
        color: 'rgba(0, 0, 0, 0.54)',
        textAlign: 'center',
        position: 'absolute',
        right: theme.spacing(3),
        display: 'flex',
        flexDirection: 'row-reverse',
        alignItems: 'center',
        justifyContent: 'center',
    },
    hiddenCollapseSpan: {
        paddingRight: theme.spacing(3.125),
    },
    productTypeIcon: (props: ElementItemCardStyleProps) => ({
        marginRight: props.isBestPracticeIconShown
            ? props.isBasedFromManufacturerOption
                ? theme.spacing(5)
                : theme.spacing(3)
            : props.isBasedFromManufacturerOption
            ? theme.spacing(3)
            : theme.spacing(0),
    }),
    productTypeCardIcon: {
        fill: '#314959',
    },
    productTypeCardIconSelected: {
        fill: theme.palette.common.white,
    },
    deactivatePTIcon: {
        fontSize: 18,
        marginRight: theme.spacing(1),
    },
}))

type BestPracticeDataExt = BestPracticeData & {
    iterationId: string
    skipPrimaryIterationUpdate: boolean
}

export interface ElementItemCardProps extends ElementItemCardStyleProps {
    id?: string
    elementId?: string
    isSelected?: boolean
    title?: string
    subtitle?: string
    expanded?: boolean
    hasSubItems?: boolean
    areSubItemsLoaded?: boolean
    isInProject?: boolean
    isInBulkAdd?: boolean
    inactive?: boolean
    onClick?: VoidFunction
    onRequestChildrenForBulkAdd?: VoidFunction
    onBulkSelect?: (type: string, isAdd: boolean) => void
    handleCollapse?: (event: React.MouseEvent<SVGSVGElement>) => void
    handleCardExpand?: (event: React.MouseEvent<SVGSVGElement>) => void
    handleBPElementTreeChange?: (
        id: string,
        treepath: string,
        isBP: boolean,
        isGBP: boolean,
        remove: boolean
    ) => void
    searchText?: string
    isSearchResult: boolean
    defaultWidth?: number | string
    isLastSubAssembly?: boolean
    isBasedFromManufacturerOption?: boolean
    isBestPractice?: boolean
    isGlobalBestPractice?: boolean
    isProjectOnly?: boolean
    isImportedToProject?: boolean
    attachedToFirmId?: string
    isSelectedForAssociation?: boolean
    isAddedForAssociation?: boolean
    contentTeamFromStudioPage?: boolean
    selectedElementFormat?: string
    disableExpand?: boolean
    isPTSelectedinSMM?: boolean
    isElementsLibraryPage?: boolean
    selectedBulkAddElements?: string[]
    elementType?: string
    isFromWorkSpacePage?: boolean
    isBPPublished?: boolean
    isProjectImported?: boolean
    /** True if the card is loaded from OM workspace and not a Masterspec item */
    isOMImportedCard?: boolean

    /** True if this card represents a Division node. */
    isDivision?: boolean
    baseElementId?: string
    updateElementList?: (elementList: ProjectElementNode[]) => void
    parentNodeId?: string | null
    elementAddedToProjectId?: string
    tabIndex?: number
    currentElementTreePath?: string
    isMasterspecClone?: boolean
    /**TODO: This is the temporary code changes for CKE ProductType elements once it will be available we can remove some of the conditions */
    parentFamilyId?: string
    currentIteration?: Iteration
    nestingLevel?: number
    isDeactivated?: boolean
}
const ElementItemCard: React.FC<ElementItemCardProps> = ({
    id,
    elementId,
    isSelected = false,
    isInBulkAdd = false,
    expanded = false,
    isInProject = false,
    title,
    subtitle,
    hasSubItems = true,
    areSubItemsLoaded = true,
    inactive = false,
    onClick,
    onRequestChildrenForBulkAdd,
    onBulkSelect,
    handleCollapse,
    searchText,
    isSearchResult,
    isL4L5Assembly = false,
    defaultWidth = 200,
    calledFromImportPage,
    isLastSubAssembly,
    isBestPractice,
    isGlobalBestPractice,
    isProjectOnly,
    isImportedToProject,
    attachedToFirmId,
    isSelectedForAssociation,
    isAddedForAssociation,
    isBasedFromManufacturerOption,
    handleCardExpand,
    handleBPElementTreeChange,
    contentTeamFromStudioPage,
    selectedElementFormat,
    disableExpand,
    isPTSelectedinSMM,
    isElementsLibraryPage,
    elementType,
    isFromWorkSpacePage = false,
    isBPPublished,
    isProjectImported,
    isOMImportedCard = false,
    isDivision = false,
    calledFromImportDuplicateFamily,
    baseElementId,
    updateElementList,
    parentNodeId,
    elementAddedToProjectId,
    tabIndex,
    currentElementTreePath,
    isMasterspecClone,
    parentFamilyId,
    currentIteration,
    nestingLevel,
    isDeactivated,
    isBetaWorkspaceToggleEnabled = false,
}) => {
    const { projectData } = React.useContext(ProjectPageContext)

    const actionRef = useRef<HTMLButtonElement | null>(null)

    const { isAdmin, isAuthor } = useReactiveVar(userInfo)
    const isOMUser = useReactiveVar(isOmWorkspaceUser)
    const [hasPublishedIterations, setHasPublishedIterations] = useState(false)

    const isBPTagDisabledInput = {
        userInfo,
        isInProject,
        contentTeamFromStudioPage,
        isProjectImported,
        isProjectOnly,
    }

    const { setUserActiveFirm } = React.useContext(AppBarContext)

    // Use useState for reactive updates
    const [firmsBPSetting, setSetting] = React.useState<boolean | null>(null)

    const { loading: userActiveFirmLoading } = useQuery<
        CurrentUserActiveFirmResponse
    >(CurrentUserActiveFirmQuery, {
        onCompleted: (data) => {
            if (data?.myActiveFirm) {
                setUserActiveFirm?.(data.myActiveFirm)
                const canShowBPSetting = canShowBestPracticeSetting(
                    data.myActiveFirm
                )
                setSetting(canShowBPSetting)
            }
        },
    })
    // Determine if Best Practices button should be shown based on workspace type
    const showBestPracticesButton = useMemo(() => {
        return isBetaWorkspaceToggleEnabled
            ? processEnv.REACT_APP_ShowBestPracticesFeatureInNewWorkspace ===
                  '1'
            : true
    }, [isBetaWorkspaceToggleEnabled])

    // checking if BP tags are disabled for editing
    const isBestPracticeEditDisabled = useIsBestPracticeTagDisabled({
        ...isBPTagDisabledInput,
        firmsBPSetting,
    })

    // checking if BP tags are disabled for viewing
    const isBestPracticeViewDisabled = useIsBestPracticeTagDisabled({
        ...isBPTagDisabledInput,
        firmsBPSetting,
        // BP tags are visible on element node even if the element node is not added to project
        // so this flag is set true by default to show BP tags on element node
        isInProject: true,
        isViewOnly: true,
    })

    const isDeactivatePTOptionHidden = React.useMemo(
        () => !(isOMUser && contentTeamFromStudioPage),
        [isOMUser, contentTeamFromStudioPage]
    )

    const isVersionSelectionHidden = React.useMemo(
        () =>
            !(
                processEnv.REACT_APP_EnableVersionControl === '1' &&
                isOMUser &&
                hasPublishedIterations &&
                !isProjectImported &&
                elementType === ElementType.PRODUCTFAMILY &&
                (isAdmin || isAuthor)
            ),
        [
            isOMUser,
            isAdmin,
            isAuthor,
            elementType,
            hasPublishedIterations,
            processEnv,
        ]
    )

    const classes = useElementCardStyles({
        isHighlighted: isSearchResult,
        darkCarret:
            (isBetaWorkspaceToggleEnabled &&
                !isInProject &&
                !isL4L5Assembly &&
                !calledFromImportPage) ||
            inactive ||
            (isL4L5Assembly && !isInProject),
        calledFromImportPage: calledFromImportPage,
        contentTeamFromStudioPage: contentTeamFromStudioPage,
        isL4L5Assembly: isL4L5Assembly,
        isOpaque: searchText ? !isSearchResult : false,
        isBasedFromManufacturerOption,
        isInProject,
        calledFromImportDuplicateFamily,
        isBetaWorkspaceToggleEnabled,
        elementType,
        isBestPracticeIconShown: isBestPractice || isGlobalBestPractice,
        isDeactivated,
    })
    const isElementFamilyBucket =
        elementType === OMElementLibraryElementType.PRODUCTFAMILYBUCKET
    const isOpaque = searchText ? !isSearchResult : false
    const [popoverAnchor, setPopoverAnchor] = React.useState<null | Element>(
        null
    )
    const [
        getBestPracticeElementData,
        {
            error: bestPracticeElementError,
            data: bestPracticeElementData,
            loading: bestPracticeElementLoading,
        },
    ] = useLazyQuery<BestPracticeDataResponse>(BestPracticeQuery, {
        fetchPolicy: 'no-cache',
        displayName: 'BestPracticeElement',
    })
    const [
        isActionPopoverDisplayed,
        setIsActionPopoverDisplayed,
    ] = React.useState(false)

    const [
        checkHasPublishedIterationsQuery,
        { loading: publishedIterationsLoading },
    ] = useLazyQuery(CheckHasPublishedIterationsQuery, {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        onCompleted: (data) => {
            if (data) {
                const elementHasIterations =
                    data?.getProjectElementNodes?.projectElementNodes?.[0]
                        ?.hasPublishedIterations ?? false
                setHasPublishedIterations(elementHasIterations)
            }
        },
    })

    const [
        getProductFamilyIterationQuery,
        { loading: familyIterationLoading },
    ] = useLazyQuery(GetProductFamilyIterationQuery, {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        onCompleted: (data) => {
            if (data) {
                setReactiveWorkspaceContents({
                    availableVersions:
                        data.getProductFamilyIterations?.familyIterations,
                })
            }
        },
    })

    useElementToTreeMapper({
        isElementsLibraryPage: isElementsLibraryPage as boolean,
        isSelected: isSelected,
        elementId: elementId ?? '',
        baseElementId: baseElementId ?? '',
        parentFamilyId: parentFamilyId ?? '',
        elementType: elementType ?? '',
        elementAddedToProjectId,
        selectedElementFormat,
    })

    const handleActionPopoverClick = React.useCallback(() => {
        setIsActionPopoverDisplayed(!isActionPopoverDisplayed)
        if (
            isOMUser &&
            !isActionPopoverDisplayed &&
            elementType === ElementType.PRODUCTFAMILY
        ) {
            checkHasPublishedIterationsQuery({
                variables: {
                    input: {
                        id: elementId,
                        projectElementType: [
                            'ASSEMBLY',
                            'PRODUCTFAMILY',
                            'PRODUCTTYPE',
                        ],
                        projectId: projectData?.getProject?.id,
                    },
                    projectId: projectData?.getProject?.id,
                },
            })
        }
        if (
            elementType === ElementType.PRODUCTTYPE &&
            !contentTeamFromStudioPage
        ) {
            const familyDetails = id
                ?.split('||')
                [id.split('||').length - 2].split('|')
            getProductFamilyIterationQuery({
                variables: {
                    input: {
                        projectId: projectData?.getProject.id,
                        familyNumber: familyDetails?.[0],
                        familyName: familyDetails?.[1],
                    },
                },
            })
        }
    }, [isActionPopoverDisplayed, elementId, elementType, id, projectData])

    const [setAddUpdateBestPractice] = useMutation<
        AddUpdateBestPracticeResponse,
        { input: Partial<BestPracticeData> }
    >(AddUpdateBestPracticeMutation, {
        onCompleted: (data: AddUpdateBestPracticeResponse) => {
            if (data && data.addUpdateBestPractice) {
                handleBPElementTreeChange?.(
                    elementId ?? '',
                    id ?? '',
                    getIsBP(data.addUpdateBestPractice),
                    data.addUpdateBestPractice?.isGlobal ?? false,
                    false
                )
            }
        },
    })

    const [setRemoveBestPractice] = useMutation<
        RemoveBestPracticeResponse,
        { input: Partial<BestPracticeData> }
    >(RemoveBestPracticeMutation, {
        onCompleted: (data: RemoveBestPracticeResponse) => {
            if (data && data.removeBestPractice) {
                handleBPElementTreeChange?.(
                    elementId ?? '',
                    id ?? '',
                    false,
                    false,
                    true
                )
            }
        },
    })

    const handleRightClick = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            event.preventDefault()
            if (
                !showBestPracticesButton ||
                isBestPracticeEditDisabled ||
                !elementId
            )
                return
            if (event.type === 'contextmenu') {
                setPopoverAnchor(event.currentTarget)
                getBestPracticeElementData({
                    variables: {
                        parentId: elementId,
                        projectId: projectData?.getProject.id,
                    },
                })
            }
        },
        [
            elementId,
            popoverAnchor,
            isBestPractice,
            isGlobalBestPractice,
            isBestPracticeEditDisabled,
            showBestPracticesButton,
        ]
    )

    const handleBestPracticesMutationData = React.useCallback(
        (mutationData: Partial<BestPracticeData> | null) => {
            setPopoverAnchor(null)
            if (mutationData) {
                const isMutationUpdated = compareBestPracticesMutation(
                    bestPracticeElementData,
                    projectData?.getProject,
                    mutationData
                )

                if (isMutationUpdated) {
                    const iterationId = currentIteration?.id
                    let mutationInput: Partial<BestPracticeDataExt> = {
                        ...mutationData,
                        parentProjectId: projectData?.getProject.id,
                        projectElementNodeId: elementId,
                        iterationId,
                        skipPrimaryIterationUpdate: false,
                    }

                    if (bestPracticeElementData?.getBestPractice?.id) {
                        mutationInput.id =
                            bestPracticeElementData?.getBestPractice?.id
                    }

                    setAddUpdateBestPractice({
                        variables: {
                            input: mutationInput,
                        },
                    })
                }
            } else {
                // removed the other condition to allow user to change the BP setting on different projects
                if (bestPracticeElementData?.getBestPractice?.id) {
                    setRemoveBestPractice({
                        variables: {
                            input: {
                                id:
                                    bestPracticeElementData?.getBestPractice
                                        ?.id,
                            },
                        },
                    })
                }
            }
            setIsActionPopoverDisplayed(false)
        },
        [
            bestPracticeElementData,
            projectData,
            elementId,
            setAddUpdateBestPractice,
            currentIteration,
        ]
    )

    const handleBestPractice = React.useCallback(() => {
        getBestPracticeElementData({
            variables: {
                parentId: elementId,
                projectId: projectData?.getProject.id,
            },
        })
    }, [getBestPracticeElementData, elementId, projectData])

    const handleBPMenuDisplay = React.useCallback(
        (event) => {
            if (isBestPracticeEditDisabled) return
            if (
                event.target?.getAttribute('data-testid') === 'dvBPMenu' ||
                event.target?.parentNode?.getAttribute('data-testid') ===
                    'dvBPMenu'
            ) {
                handleBestPractice()
            }
        },
        [
            handleBestPractice,
            isBestPracticeEditDisabled,
            isGlobalBestPractice,
            isBestPractice,
        ]
    )

    const handleElementNodeClick = React.useCallback(
        (event) => {
            if (event.ctrlKey && isFromWorkSpacePage) {
                if (
                    !isInProject && // Can't add to Quick Add if already in project
                    (!isDivision || isInBulkAdd) // Divisions can't be added by themselves (need to use Shift+Click to include children)
                ) {
                    onBulkSelect?.('single', !isInBulkAdd)
                }
            } else if (event.shiftKey && isFromWorkSpacePage) {
                if (!isInProject && elementType !== 'ASSEMBLY') {
                    if (hasSubItems && !areSubItemsLoaded) {
                        onRequestChildrenForBulkAdd?.()
                    } else {
                        onBulkSelect?.('all', !isInBulkAdd)
                    }
                }
                document?.getSelection()?.removeAllRanges()
            } else {
                onClick?.()
            }
        },
        [isInBulkAdd, elementType, isInProject, onClick, onBulkSelect]
    )
    const handlePopupMenuClick = React.useCallback(
        (type: string) => () => {
            setIsActionPopoverDisplayed(false)
            if (type === 'single') {
                onBulkSelect?.(type, !isInBulkAdd)
            } else if (!isInProject && elementType !== 'ASSEMBLY') {
                if (hasSubItems && !areSubItemsLoaded) {
                    onRequestChildrenForBulkAdd?.()
                } else {
                    onBulkSelect?.('all', !isInBulkAdd)
                }
            }
        },
        [
            isInBulkAdd,
            setIsActionPopoverDisplayed,
            onRequestChildrenForBulkAdd,
            onBulkSelect,
            isInProject,
            elementType,
            hasSubItems,
        ]
    )

    const onClickMoreEvent = React.useCallback(
        (event) => {
            event.stopPropagation()
            handleActionPopoverClick()
        },
        [isActionPopoverDisplayed, elementId, elementType, id, projectData]
    )

    const onClickMoreEventKeyDown = React.useCallback(
        (event) => {
            event.stopPropagation()
        },
        [isActionPopoverDisplayed]
    )

    const menuPlacementHeight = React.useMemo(() => {
        switch (true) {
            case !isDeactivatePTOptionHidden:
                return 70
            case isInProject && !isVersionSelectionHidden:
                return 130
            case isInProject:
                return 110
            default:
                return 80
        }
    }, [isInProject, isVersionSelectionHidden, isDeactivatePTOptionHidden])

    const popoverAnchorOrigin = React.useMemo(
        () => ({
            vertical:
                (actionRef?.current?.clientHeight ?? 0) +
                (menuPlacementHeight -
                    (isInProject && !showBestPracticesButton ? 30 : 0)),
            horizontal: (actionRef?.current?.clientWidth ?? 0) - 5,
        }),
        [menuPlacementHeight, isInProject, showBestPracticesButton]
    )

    const bulkAddMenuItems: CompleteMenuItemConfig[] = React.useMemo(
        () => [
            {
                dataTestId: 'selectForBestPractices',
                isDisabled: !isInProject,
                isHidden:
                    !showBestPracticesButton || isBestPracticeEditDisabled,
                label: 'Best Practices',
                component: (
                    <GridContainer
                        justifyContent="space-between"
                        alignItems="baseline"
                        onClick={handleBPMenuDisplay}
                        key={'bpMenu'}
                        className={classes.disabledMenuItem}
                    >
                        <Typography
                            component="span"
                            className={clsx(classes.menuText, {
                                [classes.menuTextDisabled]: isBestPracticeEditDisabled,
                            })}
                        >
                            Best Practices
                        </Typography>
                        <BestPracticesSetting
                            key={`BP-${elementId}`}
                            showBestPracticesIcons={false}
                            projectDetail={projectData?.getProject}
                            setBestPracticesMutationData={
                                handleBestPracticesMutationData
                            }
                            data={bestPracticeElementData}
                            loading={
                                bestPracticeElementLoading &&
                                familyIterationLoading &&
                                elementType === ElementType.PRODUCTTYPE
                            }
                            isBestPractice={isBestPractice}
                            isGlobalBestPractice={isGlobalBestPractice}
                            isDisabled={isBestPracticeEditDisabled}
                            moreIcon={ChevronRightIcon}
                        />
                    </GridContainer>
                ),
                smallLabel: '',
                customTextStyle: classes.menuText,
            },
            {
                dataTestId: 'omDeactivatePT',
                isDisabled: false,
                isHidden: isDeactivatePTOptionHidden,
                component: (
                    <DeactivatePTDropdown
                        isDeactivated={isDeactivated}
                        baseElementId={baseElementId}
                        id={id}
                        elementId={elementId}
                        elementType={elementType}
                        setIsActionPopoverDisplayed={
                            setIsActionPopoverDisplayed
                        }
                        updateElementList={updateElementList}
                        onNodeLoad={onClick}
                    />
                ),
                label: 'Deactivate Product Type',
                smallLabel: '',
                customTextStyle: classes.menuText,
            },
            {
                dataTestId: 'selectForVersion',
                isDisabled: false,
                isHidden: isVersionSelectionHidden,
                label: 'Office Master Selection',
                component: (
                    <WorkspaceVersionDropdown
                        id={id}
                        baseElementId={baseElementId}
                        elementId={elementAddedToProjectId}
                        projectId={projectData?.getProject?.id}
                        elementType={elementType}
                        isExpanded={
                            expanded &&
                            elementType === ElementType.PRODUCTFAMILY
                        }
                        familyElementNodeId={elementId}
                        updateElementList={updateElementList}
                        selectedElementFormat={selectedElementFormat}
                        parentNodeId={parentNodeId}
                        setIsActionPopoverDisplayed={
                            setIsActionPopoverDisplayed
                        }
                        treePath={currentElementTreePath}
                        searchText={searchText}
                        isDisabled={
                            publishedIterationsLoading ||
                            !hasPublishedIterations
                        }
                    />
                ),
                smallLabel: '',
                customTextStyle: classes.menuText,
            },
            {
                dataTestId: 'selectForBulkAddButton',
                label: isInBulkAdd
                    ? 'Remove from Quick Add'
                    : 'Select for Quick Add',
                isHidden: contentTeamFromStudioPage,
                isDisabled: isInProject,
                onClick: handlePopupMenuClick('single'),
                smallLabel: 'Ctrl + click',
            },
            {
                dataTestId: 'selectAllForBulkAddButton',
                label: isInBulkAdd
                    ? 'Remove All from Quick Add'
                    : 'Select All for Quick Add',
                isHidden: contentTeamFromStudioPage,
                isDisabled: isInProject || elementType === 'ASSEMBLY',
                onClick: handlePopupMenuClick('all'),
                smallLabel: 'Shift + click',
            },
        ],
        [
            isInProject,
            isInBulkAdd,
            handlePopupMenuClick,
            bestPracticeElementData,
            bestPracticeElementLoading,
            familyIterationLoading,
            isBestPractice,
            isGlobalBestPractice,
            projectData,
            contentTeamFromStudioPage,
            isAdmin,
            elementId,
            isBestPracticeEditDisabled,
            baseElementId,
            subtitle,
            expanded,
            elementType,
            publishedIterationsLoading,
            hasPublishedIterations,
            onClick,
            id,
            showBestPracticesButton,
        ]
    )

    const showBulkAdd = React.useMemo(() => {
        return !isInProject && isInBulkAdd && isFromWorkSpacePage
    }, [isInProject, isInBulkAdd, isFromWorkSpacePage])

    //indication on element tree if family is project imported or BP published ,
    // no impact on other components
    const showIndication = React.useMemo(() => {
        return (
            elementType === 'PRODUCTFAMILY' &&
            isFromWorkSpacePage &&
            (isBPPublished || isProjectImported)
        )
    }, [elementType, isBPPublished, isProjectImported, isFromWorkSpacePage])
    const indication = React.useMemo(() => {
        if (showIndication) {
            return (
                <div className={classes.importIndication}>
                    <div
                        className={clsx(
                            classes.importIndicationInner,
                            showBulkAdd &&
                                classes.importIndicationInnerWithBulkAdd
                        )}
                        data-testid="importIndication"
                    >
                        {isBPPublished ? 'OM' : isProjectImported ? 'PI' : ''}
                    </div>
                </div>
            )
        }
    }, [showIndication, showBulkAdd])
    const showWhiteColorExpandIcon = useMemo(
        () =>
            (isBetaWorkspaceToggleEnabled &&
                isInProject &&
                !inactive &&
                !calledFromImportPage) ||
            isSelected ||
            (isSelectedForAssociation && calledFromImportPage) ||
            (!inactive && isInProject && !calledFromImportPage) ||
            (!inactive && isSelected) ||
            (calledFromImportDuplicateFamily && isElementFamilyBucket),
        [
            isBetaWorkspaceToggleEnabled,
            isElementFamilyBucket,
            isInProject,
            inactive,
            calledFromImportPage,
            isSelected,
            isSelectedForAssociation,
            calledFromImportDuplicateFamily,
        ]
    )
    // this is to display the OM tag on element library element cards
    // no impact on other components
    const omImportedIndicationForELCards = React.useMemo(() => {
        if (!(isElementsLibraryPage && isOMImportedCard)) return null
        return (
            <HoverTooltip
                data-testid="importedIndicationELCard"
                text="Office Master"
            >
                <div
                    className={clsx(
                        classes.importIndication,
                        classes.importIndicationEL
                    )}
                >
                    <div
                        className={clsx(
                            classes.importIndicationInner,
                            classes.importIndicationInnerEL
                        )}
                        data-testid="importIndication"
                    >
                        OM
                    </div>
                </div>
            </HoverTooltip>
        )
    }, [isElementsLibraryPage, isOMImportedCard])

    const handleExpandToggle = React.useCallback(
        (event) => {
            return expanded
                ? handleCollapse?.(event)
                : calledFromImportPage
                ? handleCardExpand?.(event)
                : undefined
        },
        [expanded, calledFromImportPage, handleCollapse, handleCardExpand]
    )

    return (
        <div
            className={classes.outerPaper}
            tabIndex={tabIndex}
            onKeyDown={onEnterKeyDown(
                calledFromImportPage && tabIndex !== -1 ? onClick : undefined
            )}
        >
            {indication}
            <Paper
                onClick={
                    calledFromImportPage ? undefined : handleElementNodeClick
                }
                onKeyDown={onEnterKeyDown(
                    calledFromImportPage ? undefined : handleElementNodeClick
                )}
                tabIndex={0}
                onContextMenu={handleRightClick}
                variant="outlined"
                data-testid={`elementCard${title}`}
                className={clsx(
                    classes.cardItemContainer,
                    {
                        [classes.omDeactivatedPTCard]: isDeactivated,
                        [classes.selectableCard]:
                            !isL4L5Assembly &&
                            !isInProject &&
                            !inactive &&
                            !isSelected &&
                            !calledFromImportPage &&
                            !calledFromImportDuplicateFamily,
                        [classes.nonselectableCard]:
                            isL4L5Assembly &&
                            !isInProject &&
                            !calledFromImportPage,
                        [classes.leafNodeElement]:
                            calledFromImportPage &&
                            isLastSubAssembly &&
                            isSelectedForAssociation,
                        [classes.inProjectCard]:
                            isInProject && !isSelected && !inactive,
                        [classes.titleTextMargin]:
                            isInProject &&
                            !isSelected &&
                            !inactive &&
                            showIndication &&
                            !isBetaWorkspaceToggleEnabled,
                        [classes.selectedCard]:
                            isSelected && isInProject && !isL4L5Assembly,
                        [classes.selectedInProject]:
                            isSelected &&
                            isInProject &&
                            !contentTeamFromStudioPage,
                        [classes.selectedInSMM]:
                            isSelected && contentTeamFromStudioPage,
                        [classes.selectedSMMItem]:
                            isSelected && contentTeamFromStudioPage,
                        [classes.selectedNotInProject]:
                            isSelected &&
                            !isInProject &&
                            !isL4L5Assembly &&
                            !calledFromImportPage,
                        [classes.inactiveCard]: inactive && !isL4L5Assembly,
                        [classes.nonSelectableExpanded]:
                            isL4L5Assembly &&
                            isSelected &&
                            !isInProject &&
                            !calledFromImportPage,
                        [classes.notSearchResultCard]: isOpaque,
                        [classes.addedItemForAssociation]:
                            isAddedForAssociation && calledFromImportPage,
                        [classes.selectedPTInSMM]: isPTSelectedinSMM,
                        [classes.duplicateImportFamilyBucket]:
                            calledFromImportDuplicateFamily &&
                            isElementFamilyBucket,
                        [classes.duplicateImportLeafNode]:
                            calledFromImportDuplicateFamily &&
                            !isElementFamilyBucket,
                    },
                    'elementItemCard'
                )}
            >
                {showBulkAdd && (
                    <AddCircleOutlineIcon
                        data-testid={'plusIcon'}
                        className={clsx(
                            classes.addCircleIcon,
                            showIndication && classes.addIconWithIndication
                        )}
                        style={{
                            fontSize: isBetaWorkspaceToggleEnabled
                                ? pxToRem(20)
                                : 'auto',
                        }}
                        classes={{ root: classes.addCircleIcon }}
                    />
                )}{' '}
                {contentTeamFromStudioPage &&
                    (isInProject ||
                        (isImportedToProject && !attachedToFirmId) ||
                        (isMasterspecClone && !isBPPublished)) &&
                    isElementsLibraryPage && (
                        <CheckCircleIcon
                            data-testid="CheckCircleIcon"
                            htmlColor="#13b5ea"
                            classes={{ root: classes.checkedIcon }}
                            fontSize="inherit"
                            onClick={handleCollapse}
                        />
                    )}
                {!disableExpand && hasSubItems ? (
                    <ChevronRightIcon
                        data-testid={
                            expanded ? 'ExpandedIcon' : 'ExpandMoreIcon'
                        }
                        className={clsx(classes.cardCarret, {
                            [classes.whiteCarret]: showWhiteColorExpandIcon,
                            [classes.chevronDownIcon]: expanded,
                        })}
                        onClick={handleExpandToggle}
                        tabIndex={0}
                        aria-hidden={false}
                        onKeyDown={onEnterKeyDown(handleExpandToggle)}
                    />
                ) : isBetaWorkspaceToggleEnabled ? (
                    <div className={classes.hiddenCollapseSpan}></div>
                ) : (
                    <></>
                )}
                <div
                    onClick={calledFromImportPage ? onClick : undefined}
                    className={clsx(classes.cardTextContainer)}
                >
                    {title && (
                        <>
                            {!isSearchResult ? (
                                <Typography
                                    component="span"
                                    id="elementText"
                                    className={clsx(
                                        classes.titleTextElementCard,
                                        {
                                            [classes.elTitleText]: isElementsLibraryPage,
                                        }
                                    )}
                                    style={{
                                        width: defaultWidth,
                                    }}
                                >
                                    {isBetaWorkspaceToggleEnabled ? (
                                        showElementCardTitleInSingleLine(
                                            title,
                                            nestingLevel,
                                            elementType
                                        ) ? (
                                            <SingleLineEllipsis text={title}>
                                                {title}
                                            </SingleLineEllipsis>
                                        ) : (
                                            title
                                        )
                                    ) : (
                                        title
                                    )}
                                </Typography>
                            ) : isBetaWorkspaceToggleEnabled ? (
                                <SingleLineEllipsisForSearch
                                    searchText={searchText}
                                    text={title}
                                    isElementInProject={isInProject}
                                />
                            ) : (
                                <Highlighter
                                    className={clsx(
                                        classes.titleTextElementCard
                                    )}
                                    style={{
                                        width: defaultWidth,
                                    }}
                                    searchWords={
                                        searchText
                                            ?.split(' ')
                                            .map((word) =>
                                                escapeRegexCharacters(word)
                                            ) ?? []
                                    }
                                    textToHighlight={title}
                                    highlightStyle={{
                                        backgroundColor: 'transparent',
                                        fontWeight: 500,
                                        color: 'inherit',
                                        padding: 0,
                                    }}
                                />
                            )}
                        </>
                    )}
                    {isBetaWorkspaceToggleEnabled &&
                    elementType !== 'PRODUCTTYPE' ? (
                        <></>
                    ) : (
                        <Typography
                            component="span"
                            className={classes.elementItemFooter}
                        >
                            {!isSearchResult ? (
                                <Typography
                                    component="span"
                                    className={clsx(classes.subtitleText)}
                                    style={{ width: defaultWidth }}
                                >
                                    {subtitle}
                                </Typography>
                            ) : (
                                <Highlighter
                                    className={clsx(classes.subtitleText)}
                                    style={{ width: defaultWidth }}
                                    searchWords={
                                        searchText
                                            ?.split(' ')
                                            .map((word) =>
                                                escapeRegexCharacters(word)
                                            ) ?? []
                                    }
                                    textToHighlight={subtitle ?? ''}
                                    highlightStyle={{
                                        backgroundColor: 'transparent',
                                        fontWeight: 500,
                                        color: 'inherit',
                                        padding: 0,
                                    }}
                                />
                            )}
                        </Typography>
                    )}

                    {projectData?.getProject &&
                        projectData?.getProject?.id &&
                        !calledFromImportPage &&
                        (isBestPractice ||
                            isGlobalBestPractice ||
                            isBasedFromManufacturerOption) && (
                            <GridItem
                                className={clsx(
                                    classes.gridItem,
                                    !isBetaWorkspaceToggleEnabled ||
                                        (isBetaWorkspaceToggleEnabled &&
                                            isInProject &&
                                            !inactive &&
                                            !calledFromImportPage)
                                        ? classes.BPIcons
                                        : classes.bPIconBlack
                                )}
                            >
                                {(isBestPractice || isGlobalBestPractice) &&
                                !isBestPracticeViewDisabled ? (
                                    <BestPracticesIcons
                                        isBestPractice={isBestPractice}
                                        isGlobalBestPractice={
                                            isGlobalBestPractice
                                        }
                                    />
                                ) : (
                                    <></>
                                )}

                                {!!isBasedFromManufacturerOption ? (
                                    <Typography
                                        component="div"
                                        className={classes.manufacturerIcon}
                                    >
                                        {isBetaWorkspaceToggleEnabled ? (
                                            <ManufacturarOptionIcon
                                                iconColor={
                                                    isInProject
                                                        ? 'white'
                                                        : '#475E8A'
                                                }
                                            />
                                        ) : (
                                            <ProductListingManufacturerOptionItem></ProductListingManufacturerOptionItem>
                                        )}
                                    </Typography>
                                ) : (
                                    <></>
                                )}
                            </GridItem>
                        )}

                    {omImportedIndicationForELCards}
                </div>
                {(isFromWorkSpacePage ||
                    (isOMUser &&
                        contentTeamFromStudioPage &&
                        elementType === 'PRODUCTTYPE')) && (
                    <Button
                        tabIndex={-1}
                        endIcon={
                            <MoreVert
                                tabIndex={0}
                                className={
                                    isBetaWorkspaceToggleEnabled &&
                                    ((isL4L5Assembly &&
                                        !isInProject &&
                                        !calledFromImportPage) ||
                                        (!isL4L5Assembly &&
                                            !isInProject &&
                                            !inactive &&
                                            !isSelected &&
                                            !calledFromImportPage &&
                                            !calledFromImportDuplicateFamily) ||
                                        (isSelected &&
                                            !isInProject &&
                                            !isL4L5Assembly &&
                                            !calledFromImportPage))
                                        ? classes.moreIconBlack
                                        : classes.moreIcon
                                }
                                onKeyDown={onClickMoreEventKeyDown}
                            />
                        }
                        classes={{ text: classes.ellipsisBtn }}
                        disableRipple
                        disableFocusRipple
                        ref={actionRef}
                        onClick={onClickMoreEvent}
                        data-testid="moreActionButton"
                        aria-haspopup="true"
                        aria-expanded={isActionPopoverDisplayed}
                        aria-label="More actions button"
                    />
                )}
            </Paper>

            <WorkspacePopover
                menuItems={bulkAddMenuItems}
                anchorElement={actionRef.current}
                isActionPopoverDisplayed={isActionPopoverDisplayed}
                handleActionPopoverClick={handleActionPopoverClick}
                anchorOrigin={popoverAnchorOrigin}
            />

            <BestPracticesSetting
                onlyPopover={true}
                popoverAnchor={popoverAnchor}
                key={`BP-${elementId}`}
                showBestPracticesIcons={false}
                projectDetail={projectData?.getProject}
                setBestPracticesMutationData={handleBestPracticesMutationData}
                data={bestPracticeElementData}
                loading={
                    bestPracticeElementLoading &&
                    familyIterationLoading &&
                    elementType === ElementType.PRODUCTTYPE
                }
                isBestPractice={isBestPractice}
                isGlobalBestPractice={isGlobalBestPractice}
                isDisabled={isBestPracticeEditDisabled}
                moreIcon={ChevronRightIcon}
            />
        </div>
    )
}

export default ElementItemCard
